import '../../../../pages/Admin/Admin.scss';
import '../../../Tabs.scss';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import './EditTrackmanParameterV2.scss';

import { Button, Checkbox, Input, LoadingPlaceholder, Select } from '@trackman/web-shared-components';
import Box from '@mui/material/Box/Box';
import { ContentState, EditorState } from 'draft-js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ImageUploader from 'components/Admin/ImageUploader/ImageUploader';
import Paragraph from 'components/TextComps/Paragraph';
import { Editor } from 'react-draft-wysiwyg';
import { ReactComponent as TrashIcon } from 'images/icons/Trash.svg';
import { AdminPagesRelativeRoutes } from 'Routes';
import { TranslationStatusChip } from 'pages/Admin/ModulesV2/TranslationStatusChip/TranslationStatusChip';
import { useCreateTrackmanParameterV2 } from 'queryHooks/Admin/TrackmanParametersV2/useCreateTrackmanParameterV2';
import { useLegendImagesV2 } from 'queryHooks/Admin/TrackmanParametersV2/useLegendImagesV2';
import { useResyncTrackmanParameterTranslationV2 } from 'queryHooks/Admin/TrackmanParametersV2/useResyncTrackmanParameterTranslationV2';
import { useSendTrackmanParameterToTranslationV2 } from 'queryHooks/Admin/TrackmanParametersV2/useSendTrackmanParameterToTranslationV2';
import { useTrackmanParameterCategories } from 'queryHooks/Admin/TrackmanParameterCategories/useTrackmanParameterCategories';
import { useTrackmanParameterV2 } from 'queryHooks/Admin/TrackmanParametersV2/useTrackmanParameterV2';
import { useUpdateTrackmanParameterV2 } from 'queryHooks/Admin/TrackmanParametersV2/useUpdateTrackmanParameterV2';
import { useUploadLegendImageV2 } from 'queryHooks/Admin/TrackmanParametersV2/useUploadLegendImageV2';
import { useUploadTrackmanParameterMainImageV2 } from 'queryHooks/Admin/TrackmanParametersV2/useUploadTrackmanParameterMainImageV2';
import { TranslationProgress, TranslationStatus } from 'types/admin/modulesV2';
import {
  TrackmanParameterV2Content,
  TrackmanParameterV2LegendItem,
  TrackmanParameterV2Video,
} from 'types/admin/trackmanParametersV2';
import { ParameterMediaType } from 'types/admin/trackmanParameters';
import { convertContent, modifyPastedText } from 'utilities/textEditorHelper';
import { threeDModelUrlValidator, videoUrlValidator } from 'utilities/urlValidators';
import htmlToDraft from 'html-to-draftjs';
import useSetPageTitle from 'utilities/useSetPageTitle';

const emptyContent: TrackmanParameterV2Content = {
  title: '',
  mediaType: 'Image',
  definition: '',
  usageInfo: '',
  articlesInfo: '',
  mainImgUrl: '',
  threeDModelUrl: '',
  legendItems: [],
  videos: [],
  averageTourDataVisible: {
    pga: false,
    lpga: false,
  },
  visible: false,
};

const mediaTypes: { label: string; value: ParameterMediaType }[] = [
  { label: 'Image', value: 'Image' },
  { label: '3D Model', value: 'ThreeDModel' },
];

const canResync = (status: TranslationStatus): boolean =>
  status === 'InTranslation' || status === 'Partial' || status === 'Translated';

const getLanguageLabel = (code: string): string => {
  try {
    const displayNames = new Intl.DisplayNames(['en'], { type: 'language' });
    const name = displayNames.of(code);
    return name ? `${name} (${code})` : code.toUpperCase();
  } catch {
    return code.toUpperCase();
  }
};

type TranslationProgressSectionProps = {
  parameterId: string;
  progress?: TranslationProgress;
};

const TranslationProgressSection = ({ parameterId, progress }: TranslationProgressSectionProps) => {
  const resync = useResyncTrackmanParameterTranslationV2();
  const languages = progress ? Object.entries(progress.statusPerLanguage) : [];

  if (languages.length === 0) {
    return null;
  }

  return (
    <div className='admin-edit-section translation-progress-section'>
      <h2>Translations</h2>
      <div className='translation-progress'>
        {languages.map(([language, status]) => (
          <div key={language} className='translation-progress-row'>
            <div className='language'>{getLanguageLabel(language)}</div>
            <div className='status'>
              <TranslationStatusChip status={status} />
            </div>
            <div className='actions'>
              <Button
                variant='text'
                intent='secondary'
                size='small'
                onClick={() => resync.mutate({ id: parameterId, language })}
                disabled={!canResync(status) || resync.isPending}
                isLoading={resync.isPending && resync.variables?.language === language}
              >
                Resync
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

type RichTextInputProps = {
  label: string;
  value: string;
  disabled: boolean;
  resetKey: string;
  onChange: (value: string) => void;
};

const RichTextInput = ({ label, value, disabled, resetKey, onChange }: RichTextInputProps) => {
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
  const lastResetKey = useRef<string>();

  useEffect(() => {
    if (lastResetKey.current === resetKey) {
      return;
    }

    lastResetKey.current = resetKey;
    const contentBlock = htmlToDraft(value || '');
    if (contentBlock) {
      const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
      setEditorState(EditorState.createWithContent(contentState));
    } else {
      setEditorState(EditorState.createEmpty());
    }
  }, [resetKey, value]);

  const handlePastedText = (text: string, html: string | undefined, state: EditorState): boolean => {
    const newContent = modifyPastedText(text, html, state);
    const newState = EditorState.push(state, newContent, 'insert-fragment');
    setEditorState(newState);
    onChange(convertContent(newState));
    return true;
  };

  const handleEditorStateChange = (newState: EditorState) => {
    setEditorState(newState);
    onChange(convertContent(newState));
  };

  return (
    <div className='text-editor-container'>
      <Paragraph>
        <>{label}</>
      </Paragraph>
      <Editor
        editorState={editorState}
        toolbarClassName='question-editor-toolbar'
        wrapperClassName='question-editor-wrapper'
        editorClassName='question-editor'
        onEditorStateChange={handleEditorStateChange}
        handlePastedText={handlePastedText}
        readOnly={disabled}
        toolbarHidden={disabled}
      />
    </div>
  );
};

export const EditTrackmanParameterV2 = () => {
  const parameterId = useParams().parameterId ?? '';
  const navigate = useNavigate();
  const { data: parameter, isPending: parameterPending } = useTrackmanParameterV2(parameterId);
  const { data: categories = [], isPending: categoriesPending } = useTrackmanParameterCategories();
  const createParameter = useCreateTrackmanParameterV2();
  const updateParameter = useUpdateTrackmanParameterV2();
  const sendToTranslation = useSendTrackmanParameterToTranslationV2();
  const uploadMainImage = useUploadTrackmanParameterMainImageV2();
  const { data: legendImages = [] } = useLegendImagesV2();
  const uploadLegendImage = useUploadLegendImageV2();
  const legendImageInputRef = useRef<HTMLInputElement>(null);

  const [content, setContent] = useState<TrackmanParameterV2Content>(emptyContent);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [subcategoryId, setSubcategoryId] = useState<string | null>(null);
  const [contentVersion, setContentVersion] = useState(0);

  const isExistingParameter = !!parameterId;
  const isLoading = (isExistingParameter && parameterPending) || categoriesPending;
  const isSaving = createParameter.isPending || updateParameter.isPending;
  const translationStatus = parameter?.translationProgress.status;
  const editorResetKey = isExistingParameter ? `${parameterId}-${contentVersion}` : 'new';

  useSetPageTitle({ title: isExistingParameter ? content.title || 'Edit Trackman Parameter' : 'New Trackman Parameter' });

  useEffect(() => {
    if (parameter) {
      setContent({ ...emptyContent, ...parameter.content });
      setCategoryId(parameter.categoryId ?? null);
      setSubcategoryId(parameter.subcategoryId ?? null);
      setContentVersion((version) => version + 1);
    }
  }, [parameter]);

  const selectedCategory = useMemo(
    () => categories.find((category) => category.id === categoryId) ?? null,
    [categories, categoryId]
  );

  const isThreeDModelUrlInvalid = content.mediaType === 'ThreeDModel' && !threeDModelUrlValidator(content.threeDModelUrl ?? '');
  const hasInvalidVideos = content.videos.some((video) => !video.label || !video.videoUrl || videoUrlValidator(video.videoUrl));
  const disableSave = !content.title || isThreeDModelUrlInvalid || hasInvalidVideos || isSaving || isLoading;

  const updateContent = (patch: Partial<TrackmanParameterV2Content>) => {
    setContent((current) => ({ ...current, ...patch }));
  };

  const updateLegendItem = (id: string, patch: Partial<TrackmanParameterV2LegendItem>) => {
    setContent((current) => ({
      ...current,
      legendItems: current.legendItems.map((legendItem) => (legendItem.id === id ? { ...legendItem, ...patch } : legendItem)),
    }));
  };

  const updateVideo = (id: string, patch: Partial<TrackmanParameterV2Video>) => {
    setContent((current) => ({
      ...current,
      videos: current.videos.map((video) => (video.id === id ? { ...video, ...patch } : video)),
    }));
  };

  const handleUploadMainImage = (file: File) => {
    uploadMainImage.mutate(file, { onSuccess: (mainImgUrl) => updateContent({ mainImgUrl }) });
  };

  const toggleLegendImage = (imageUrl: string) => {
    setContent((current) => {
      const isSelected = current.legendItems.some((legendItem) => legendItem.imageUrl === imageUrl);
      return {
        ...current,
        legendItems: isSelected
          ? current.legendItems.filter((legendItem) => legendItem.imageUrl !== imageUrl)
          : [...current.legendItems, { id: crypto.randomUUID(), imageUrl, description: '' }],
      };
    });
  };

  const removeLegendItem = (id: string) => {
    setContent((current) => ({
      ...current,
      legendItems: current.legendItems.filter((legendItem) => legendItem.id !== id),
    }));
  };

  const handleUploadLegendImage = () => {
    const file = legendImageInputRef.current?.files?.[0];
    if (file) {
      uploadLegendImage.mutate(file);
    }
    if (legendImageInputRef.current) {
      legendImageInputRef.current.value = '';
    }
  };

  const save = () => {
    const id = parameterId || crypto.randomUUID();
    const payload = {
      id,
      categoryId,
      subcategoryId,
      content: {
        ...content,
        legendItems: content.legendItems.filter((legendItem) => legendItem.imageUrl || legendItem.description),
        videos: content.videos.filter((video) => video.videoUrl || video.label),
      },
    };

    if (parameterId) {
      updateParameter.mutate(payload);
    } else {
      createParameter.mutate(payload, {
        onSuccess: () => navigate(`/Admin/${AdminPagesRelativeRoutes.TrackmanParametersEditorV2}/${id}`),
      });
    }
  };

  return (
    <>
      <div className='default-page-header'>
        <h1 className='page-title'>{isExistingParameter ? content.title || 'Edit Trackman Parameter' : 'New Trackman Parameter'}</h1>
      </div>
      <Box className='tabs-container no-tabs'>
        <div className='buttons'>
          {isExistingParameter && <TranslationStatusChip status={translationStatus} />}
          {isExistingParameter && (
            <Button
              variant='contained'
              intent='secondary'
              onClick={() => sendToTranslation.mutate(parameterId)}
              disabled={translationStatus === 'InTranslation' || isLoading || isSaving || sendToTranslation.isPending}
              isLoading={sendToTranslation.isPending}
            >
              Send to translation
            </Button>
          )}
          <Button
            variant='contained'
            intent='tertiary'
            onClick={() => navigate(`/Admin/${AdminPagesRelativeRoutes.TrackmanParametersListV2}`)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button variant='contained' intent='primary' onClick={save} disabled={disableSave} isLoading={isSaving}>
            Save
          </Button>
        </div>
      </Box>
      <div className='admin-content-container edit-trackman-parameter-v2'>
        {isLoading ? (
          <LoadingPlaceholder />
        ) : (
          <>
            <div className='admin-edit-section'>
              <h2>Settings</h2>
              <div className='checkboxes-container'>
                <Checkbox label='Visible' checked={content.visible} onChange={() => updateContent({ visible: !content.visible })} />
                <Checkbox
                  label='PGA tour averages'
                  checked={content.averageTourDataVisible.pga}
                  onChange={() =>
                    updateContent({
                      averageTourDataVisible: {
                        ...content.averageTourDataVisible,
                        pga: !content.averageTourDataVisible.pga,
                      },
                    })
                  }
                />
                <Checkbox
                  label='LPGA tour averages'
                  checked={content.averageTourDataVisible.lpga}
                  onChange={() =>
                    updateContent({
                      averageTourDataVisible: {
                        ...content.averageTourDataVisible,
                        lpga: !content.averageTourDataVisible.lpga,
                      },
                    })
                  }
                />
              </div>
            </div>

            <div className='admin-edit-section'>
              <h2>General</h2>
              <div className='inputs-container wide-input'>
                <Input label='Title' required value={content.title} disabled={isSaving} onChange={(e) => updateContent({ title: e.target.value })} />
                <Select
                  label='Category'
                  value={categoryId ?? ''}
                  disabled={isSaving}
                  selectOptions={[
                    { label: 'Unassigned', value: '' },
                    ...categories.map((category) => ({ label: category.title, value: category.id })),
                  ]}
                  onChange={(e) => {
                    setCategoryId((e.target.value as string) || null);
                    setSubcategoryId(null);
                  }}
                />
                <Select
                  label='Subcategory'
                  value={subcategoryId ?? ''}
                  disabled={isSaving || !selectedCategory}
                  selectOptions={[
                    { label: 'Unassigned', value: '' },
                    ...(selectedCategory?.subcategories ?? []).map((subcategory) => ({ label: subcategory.title, value: subcategory.id })),
                  ]}
                  onChange={(e) => setSubcategoryId((e.target.value as string) || null)}
                />
              </div>
            </div>

            <div className='admin-edit-section'>
              <h2>Parameter media</h2>
              {content.mediaType === 'ThreeDModel' && (
                <Paragraph>
                  <span className={isThreeDModelUrlInvalid ? 'error' : ''}>
                    Please add only URL-s for models created in <strong>https://spline.design/</strong> service with a{' '}
                    <strong>scene.splinecode</strong> format.
                  </span>
                </Paragraph>
              )}
              <Select
                label='Media Type'
                value={content.mediaType}
                disabled={isSaving}
                selectOptions={mediaTypes}
                onChange={(e) => {
                  const mediaType = e.target.value as ParameterMediaType;
                  updateContent(
                    mediaType === 'ThreeDModel'
                      ? { mediaType, mainImgUrl: '', legendItems: [] }
                      : { mediaType, threeDModelUrl: '', legendItems: [] }
                  );
                }}
              />
              <div className='parameter-media'>
                {content.mediaType === 'Image' ? (
                  <ImageUploader
                    title='Main parameter image'
                    imageData={undefined}
                    imageUrl={content.mainImgUrl}
                    isSaving={isSaving || uploadMainImage.isPending}
                    handleUploadImage={handleUploadMainImage}
                  />
                ) : (
                  <Input
                    className={isThreeDModelUrlInvalid ? 'error' : ''}
                    label='3D Model URL'
                    value={content.threeDModelUrl ?? ''}
                    disabled={isSaving}
                    onChange={(e) => updateContent({ threeDModelUrl: e.target.value })}
                  />
                )}
              </div>
            </div>

            <div className='admin-edit-section'>
              <h2>Legend items</h2>
              <div className='parameter-media'>
                <Paragraph>
                  <>Select legend images</>
                </Paragraph>
                <div className='legend-images'>
                  {legendImages.map((legendImage) => (
                    <div key={legendImage.id} className='legend-image' onClick={() => toggleLegendImage(legendImage.url)}>
                      <img src={legendImage.url} alt={legendImage.name} />
                    </div>
                  ))}
                  <div className='inputs-container'>
                    <input
                      type='file'
                      accept='image/*'
                      ref={legendImageInputRef}
                      style={{ display: 'none' }}
                      onChange={handleUploadLegendImage}
                    />
                    <Button
                      className='styled-button secondary'
                      label='Add'
                      intent='secondary'
                      onClick={() => legendImageInputRef.current?.click()}
                      disabled={isSaving || uploadLegendImage.isPending}
                    />
                  </div>
                </div>
              </div>
              <div className='added-legend-items'>
                {content.legendItems.map((legendItem) => (
                  <div key={legendItem.id} className='legend-item'>
                    <div className='legend-title'>
                      <Paragraph>
                        <>Legend image:</>
                      </Paragraph>
                      <div className='legend-image'>
                        <img src={legendItem.imageUrl} alt='legend' />
                      </div>
                      <button className='delete-button' onClick={() => removeLegendItem(legendItem.id)}>
                        <TrashIcon />
                      </button>
                    </div>
                    <div className='inputs-container wide-input'>
                      <Input
                        label='Description'
                        value={legendItem.description}
                        disabled={isSaving}
                        onChange={(e) => updateLegendItem(legendItem.id, { description: e.target.value })}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className='admin-edit-section video-settings-wrapper'>
              <h2>Parameter videos</h2>
              <Paragraph>
                <span className={hasInvalidVideos ? 'error' : ''}>
                  Please add only ID of the video, you can find it in the YouTube link eg. https://www.youtube.com/watch?v=
                  <strong>xZoMV_ap4s4</strong>
                </span>
              </Paragraph>
              <div className='list-items'>
                {content.videos.map((video) => (
                  <div className='inputs-container list-item-row wide-input' key={video.id}>
                    <Input
                      label='Video title'
                      value={video.label}
                      disabled={isSaving}
                      onChange={(e) => updateVideo(video.id, { label: e.target.value })}
                    />
                    <Input
                      label='Video ID'
                      value={video.videoUrl}
                      disabled={isSaving}
                      error={!video.videoUrl || videoUrlValidator(video.videoUrl)}
                      onChange={(e) => updateVideo(video.id, { videoUrl: e.target.value })}
                    />
                    <button
                      className='delete-button'
                      onClick={() =>
                        setContent((current) => ({
                          ...current,
                          videos: current.videos.filter((item) => item.id !== video.id),
                        }))
                      }
                    >
                      <TrashIcon />
                    </button>
                  </div>
                ))}
              </div>
              <button
                className='styled-button secondary'
                onClick={() =>
                  setContent((current) => ({
                    ...current,
                    videos: [...current.videos, { id: crypto.randomUUID(), videoUrl: '', label: '' }],
                  }))
                }
                disabled={isSaving}
              >
                Add video
              </button>
            </div>

            <div className='admin-edit-section'>
              <h2>Definition</h2>
              <RichTextInput
                label='Source content'
                value={content.definition}
                disabled={isSaving}
                resetKey={`${editorResetKey}-definition`}
                onChange={(definition) => updateContent({ definition })}
              />
            </div>

            <div className='admin-edit-section'>
              <h2>Usage info</h2>
              <RichTextInput
                label='Source content'
                value={content.usageInfo}
                disabled={isSaving}
                resetKey={`${editorResetKey}-usageInfo`}
                onChange={(usageInfo) => updateContent({ usageInfo })}
              />
            </div>

            <div className='admin-edit-section'>
              <h2>Additional articles</h2>
              <RichTextInput
                label='Source content'
                value={content.articlesInfo}
                disabled={isSaving}
                resetKey={`${editorResetKey}-articlesInfo`}
                onChange={(articlesInfo) => updateContent({ articlesInfo })}
              />
            </div>

            {isExistingParameter && <TranslationProgressSection parameterId={parameterId} progress={parameter?.translationProgress} />}
          </>
        )}
      </div>
    </>
  );
};

export default EditTrackmanParameterV2;
